import {
  emptyStartingTeacherProgress,
  skillCompletion,
  startingTeacherRoute,
  type StartingTeacherNoteSubject,
  type StartingTeacherProgress,
  type StartingTeacherTaskId,
  type TopicStatus,
} from "./starting-teacher-route.js";

const storageKey = (employeeId: string) => `peer-start.starting-teacher.${employeeId}`;

const startingTeacherTaskIds = ["course", "learn", "practice", "chat"] as const satisfies readonly StartingTeacherTaskId[];

export function allStartingTeacherTasksAchieved(progress: StartingTeacherProgress, skillId: string) {
  const tasks = progress.achievedTasks[skillId];
  return startingTeacherTaskIds.every((taskId) => tasks?.[taskId]);
}

function demoProgress(employeeId: string): StartingTeacherProgress {
  const progress = emptyStartingTeacherProgress();
  if (employeeId !== "noor") return progress;
  return {
    ...progress,
    hasMentor: true,
    mentorName: "Femke Kuipers",
    mentorMeetingDate: "2026-10-24",
    mentorFrequency: "fortnightly",
    courses: { instructie: "done", klassen: "done" },
    topics: Object.fromEntries(
      ["les", "verwachtingen", "verschillen", "middelen", "regie", "groep", "routines"].map((topicId) => [
        topicId,
        { note: "", status: "self" as const },
      ]),
    ),
    skillCompletedAt: {
      instructie: "2026-10-14T12:00:00",
      klassen: "2026-10-08T12:00:00",
    },
  };
}

export function readStartingTeacherProgress(employeeId: string): StartingTeacherProgress {
  if (!employeeId || typeof localStorage === "undefined") return demoProgress(employeeId);
  try {
    const raw = localStorage.getItem(storageKey(employeeId));
    if (!raw) return demoProgress(employeeId);
    const parsed = JSON.parse(raw) as Partial<StartingTeacherProgress>;
    const empty = emptyStartingTeacherProgress();
    const progress = {
      ...empty,
      ...parsed,
      courses: { ...empty.courses, ...parsed.courses },
      topics: { ...empty.topics, ...parsed.topics },
      completedSkills: { ...empty.completedSkills, ...parsed.completedSkills },
      skillCompletedAt: { ...empty.skillCompletedAt, ...parsed.skillCompletedAt },
      skillVerifiedAt: { ...empty.skillVerifiedAt, ...parsed.skillVerifiedAt },
      skillStartedAt: { ...empty.skillStartedAt, ...parsed.skillStartedAt },
      achievedTasks: { ...empty.achievedTasks, ...parsed.achievedTasks },
      quizScores: { ...empty.quizScores, ...parsed.quizScores },
      notes: Array.isArray(parsed.notes)
        ? parsed.notes.map((note) => ({ ...note, sharedWithCoach: note.sharedWithCoach === true }))
        : empty.notes,
    };
    let migrated = false;
    for (const competency of startingTeacherRoute) {
      for (const skill of competency.skills) {
        if (skillCompletion(skill, progress).complete && !progress.skillCompletedAt[skill.id]) {
          progress.skillCompletedAt[skill.id] = new Date().toISOString();
          migrated = true;
        }
      }
    }
    if (migrated) writeStartingTeacherProgress(employeeId, progress);
    return progress;
  } catch {
    return demoProgress(employeeId);
  }
}

export function writeStartingTeacherProgress(employeeId: string, progress: StartingTeacherProgress): void {
  localStorage.setItem(storageKey(employeeId), JSON.stringify(progress));
}

export function withAchievedTask(
  progress: StartingTeacherProgress,
  skillId: string,
  taskId: StartingTeacherTaskId,
): StartingTeacherProgress {
  const next: StartingTeacherProgress = {
    ...progress,
    achievedTasks: {
      ...progress.achievedTasks,
      [skillId]: { ...progress.achievedTasks[skillId], [taskId]: true },
    },
  };
  if (!allStartingTeacherTasksAchieved(next, skillId) || next.completedSkills[skillId]) return next;
  return withCompletedSkill(next, skillId, "self");
}

export function withCourse(
  progress: StartingTeacherProgress,
  skillId: string,
  state: "done" | "skipped",
): StartingTeacherProgress {
  return { ...progress, courses: { ...progress.courses, [skillId]: state } };
}

export function withCoachNote(
  progress: StartingTeacherProgress,
  subject: StartingTeacherNoteSubject,
  text: string,
  sharedWithCoach = false,
): StartingTeacherProgress {
  const trimmed = text.trim();
  if (!trimmed) return progress;
  return {
    ...progress,
    notes: [
      ...progress.notes,
      {
        id: `${subject.kind}:${subject.id}:${progress.notes.length}:${Date.now().toString(36)}`,
        subjectKind: subject.kind,
        subjectId: subject.id,
        text: trimmed,
        createdAt: new Date().toISOString(),
        sharedWithCoach,
      },
    ],
  };
}

export function withNote(
  progress: StartingTeacherProgress,
  topicId: string,
  note: string,
): StartingTeacherProgress {
  const previous = progress.topics[topicId];
  const status: TopicStatus = previous && previous.status !== "open" ? previous.status : "noted";
  return {
    ...progress,
    topics: { ...progress.topics, [topicId]: { status: note.trim() ? status : "open", note } },
  };
}

export function withClosedTopic(progress: StartingTeacherProgress, topicId: string): StartingTeacherProgress {
  const previous = progress.topics[topicId];
  const status: TopicStatus = progress.hasMentor ? "mentor" : "self";
  const next: StartingTeacherProgress = {
    ...progress,
    topics: {
      ...progress.topics,
      [topicId]: { note: previous?.note ?? "", status },
    },
  };
  const skill = startingTeacherRoute
    .flatMap((competency) => competency.skills)
    .find((entry) => entry.topics.some((topicRef) => topicRef.id === topicId));
  if (!skill || !skillCompletion(skill, next).complete || next.skillCompletedAt[skill.id]) return next;
  return {
    ...next,
    skillCompletedAt: { ...next.skillCompletedAt, [skill.id]: new Date().toISOString() },
  };
}

export function withQuizScore(
  progress: StartingTeacherProgress,
  skillId: string,
  percent: number,
): StartingTeacherProgress {
  const previous = progress.quizScores[skillId];
  return {
    ...progress,
    quizScores: {
      ...progress.quizScores,
      [skillId]: previous === 100 ? 100 : percent,
    },
  };
}

export function withCompletedSkill(
  progress: StartingTeacherProgress,
  skillId: string,
  source: "self" | "quiz" | "verified",
): StartingTeacherProgress {
  return {
    ...progress,
    completedSkills: { ...progress.completedSkills, [skillId]: source },
    skillCompletedAt: {
      ...progress.skillCompletedAt,
      [skillId]: progress.skillCompletedAt[skillId] ?? new Date().toISOString(),
    },
  };
}

export function withActiveTopic(
  progress: StartingTeacherProgress,
  competencyId: string,
  skillId: string,
  topicId: string,
): StartingTeacherProgress {
  return {
    ...progress,
    activeCompetencyId: competencyId,
    activeSkillId: skillId,
    activeTopicId: topicId,
    skillStartedAt: {
      ...progress.skillStartedAt,
      [skillId]: progress.skillStartedAt[skillId] ?? new Date().toISOString(),
    },
  };
}

export function verifyStartingTeacherSkill(employeeId: string, skillId: string): StartingTeacherProgress {
  const progress = readStartingTeacherProgress(employeeId);
  const located = startingTeacherRoute
    .flatMap((competency) => competency.skills)
    .find((skill) => skill.id === skillId);
  if (!located || !skillCompletion(located, progress).complete) return progress;
  const next = {
    ...progress,
    skillVerifiedAt: { ...progress.skillVerifiedAt, [skillId]: new Date().toISOString() },
  };
  writeStartingTeacherProgress(employeeId, next);
  return next;
}

export function clearStartingTeacherVerifications(employeeIds: readonly string[]): void {
  for (const employeeId of employeeIds) {
    const progress = readStartingTeacherProgress(employeeId);
    writeStartingTeacherProgress(employeeId, { ...progress, skillVerifiedAt: {} });
  }
}
